import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const ZONES = [
  { value: "homepage_top", label: "Homepage — top (below stats)" },
  { value: "between_offers", label: "Between offers (in feed grid)" },
  { value: "between_stores", label: "Between stores (in Popular Stores)" },
];

export default function SuperAdminBannersPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ image_url: "", target_url: "", placement_zone: "between_offers", label: "Sponsored", sponsor_name: "", is_active: true });
  const [busy, setBusy] = useState(false);

  const load = () => api.adminListBanners().then((r) => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.image_url || !form.target_url) return toast.error("Image + target URL required");
    setBusy(true);
    try {
      await api.superadminCreateBanner(form);
      toast.success("Banner created");
      setOpen(false);
      setForm({ image_url: "", target_url: "", placement_zone: "between_offers", label: "Sponsored", sponsor_name: "", is_active: true });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };
  const toggle = async (r) => {
    try { await api.superadminUpdateBanner(r.id, { is_active: !r.is_active }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const del = async (r) => {
    if (!confirm(`Delete banner from ${r.sponsor_name || "—"}?`)) return;
    try { await api.superadminDeleteBanner(r.id); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold font-heading">Banner ads</h1>
          <p className="text-sm text-muted-foreground">House-branded ads inserted into homepage + Explore. Randomised per fetch, impressions counted on visible mount.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="banner-add">Add banner</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New banner</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="banner-image" />
              <Input placeholder="Target URL (opens in new tab)" value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} data-testid="banner-target" />
              <Input placeholder="Sponsor name (shown on badge)" value={form.sponsor_name} onChange={(e) => setForm({ ...form, sponsor_name: e.target.value })} data-testid="banner-sponsor" />
              <Input placeholder="Label eyebrow (default: Sponsored)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
              <Select value={form.placement_zone} onValueChange={(v) => setForm({ ...form, placement_zone: v })}>
                <SelectTrigger data-testid="banner-zone"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ZONES.map((z) => <SelectItem key={z.value} value={z.value}>{z.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={busy || !form.image_url || !form.target_url}>{busy ? "Saving…" : "Create"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="py-3 px-4 text-left">Preview</th><th className="py-3 px-4 text-left">Sponsor</th><th className="py-3 px-4">Zone</th><th className="py-3 px-4">Impr</th><th className="py-3 px-4">Clicks</th><th className="py-3 px-4">Status</th><th className="py-3 px-4 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={7} className="py-10 text-center text-muted-foreground">No banners yet.</td></tr>
              : rows.map((r) => (
                <tr key={r.id} className="border-t border-border" data-testid={`banner-row-${r.id}`}>
                  <td className="py-3 px-4"><img src={r.image_url} alt="" className="h-10 w-20 object-cover rounded border" onError={(e) => { e.currentTarget.style.display = "none"; }} /></td>
                  <td className="py-3 px-4 font-medium">{r.sponsor_name || "—"}</td>
                  <td className="py-3 px-4 text-center"><Badge variant="outline" className="text-[10px]">{r.placement_zone}</Badge></td>
                  <td className="py-3 px-4 text-center tabular-nums">{r.impression_count || 0}</td>
                  <td className="py-3 px-4 text-center tabular-nums">{r.click_count || 0}</td>
                  <td className="py-3 px-4 text-center"><Badge variant={r.is_active ? "default" : "secondary"}>{r.is_active ? "Live" : "Off"}</Badge></td>
                  <td className="py-3 px-4 text-right space-x-2">
                    <Button size="sm" variant="outline" onClick={() => toggle(r)}>{r.is_active ? "Pause" : "Enable"}</Button>
                    <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => del(r)}>Delete</Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
